import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/pages/portal/components/PortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import { getMyReviews, type OwnerReview } from "@/services/reviewService";
import { ApiError } from "@/services/http";
import OwnerReviewCard from "./components/OwnerReviewCard";

type Tab = "all" | "unreplied" | "pending" | "flagged";

export default function PortalReviewsPage() {
  const { accessToken, isDemoMode } = useAuth();
  const [reviews, setReviews] = useState<OwnerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [tab,     setTab]     = useState<Tab>("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getMyReviews(accessToken);
      setReviews(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We could not load your reviews. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  function handleReplied(id: number, reply: string) {
    setReviews((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ownerReply: reply, repliedAt: new Date().toISOString() } : r)),
    );
  }

  const total      = reviews.length;
  const unreplied  = reviews.filter((r) => !r.ownerReply).length;
  const pending    = reviews.filter((r) => r.status === "pending").length;
  const flagged    = reviews.filter((r) => r.flagCount > 0).length;
  const rated      = reviews.filter((r) => r.status === "approved");
  const avgRating  = rated.length
    ? (rated.reduce((sum, r) => sum + r.rating, 0) / rated.length).toFixed(1)
    : "—";

  const stats = [
    { label: "Total reviews", value: total,     icon: "ri-chat-3-line",      color: "text-primary-600",   bg: "bg-primary-50" },
    { label: "Awaiting reply", value: unreplied, icon: "ri-reply-line",      color: "text-amber-600",     bg: "bg-amber-50" },
    { label: "Average rating", value: avgRating, icon: "ri-star-line",       color: "text-accent-600",    bg: "bg-accent-50" },
    { label: "Reported",       value: flagged,   icon: "ri-flag-2-line",     color: "text-rose-600",      bg: "bg-rose-50" },
  ];

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all",       label: "All",           count: total },
    { key: "unreplied", label: "Awaiting reply", count: unreplied },
    { key: "pending",   label: "In review",     count: pending },
    { key: "flagged",   label: "Reported",      count: flagged },
  ];

  const filtered = reviews.filter((r) => {
    if (tab === "all")       return true;
    if (tab === "unreplied") return !r.ownerReply;
    if (tab === "pending")   return r.status === "pending";
    if (tab === "flagged")   return r.flagCount > 0;
    return true;
  });

  return (
    <PortalLayout>
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">Reviews</h1>
            <p className="mt-1 text-sm text-foreground-600">
              See what customers say about your listings and reply publicly to build trust.
            </p>
          </div>
          {isDemoMode && (
            <span className="shrink-0 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
              Demo Mode
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${s.bg}`}>
                <i className={`${s.icon} text-lg ${s.color}`} />
              </div>
              <p className="font-heading text-2xl font-bold text-foreground-950">{loading ? "—" : s.value}</p>
              <p className="mt-0.5 text-xs text-foreground-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />{error}
            </span>
            <button
              type="button"
              onClick={load}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        )}

        {/* Reviews */}
        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
            <h2 className="font-heading text-base font-bold text-foreground-950">Customer feedback</h2>
            <span className="text-xs text-foreground-500">
              {filtered.length} review{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 overflow-x-auto border-b border-background-200 px-5 pt-3">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`cursor-pointer whitespace-nowrap border-b-2 px-3 pb-2.5 text-xs font-semibold transition-colors ${
                  tab === t.key
                    ? "border-primary-500 text-primary-700"
                    : "border-transparent text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {t.label} ({t.count})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-chat-3-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm font-medium text-foreground-700">No reviews here yet</p>
              <p className="mt-1 text-xs text-foreground-500">
                Customer reviews will appear here as they come in.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {filtered.map((review) => (
                <OwnerReviewCard
                  key={review.id}
                  review={review}
                  token={accessToken}
                  onReplied={handleReplied}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}